import { createSvgEl } from '@learnforge/demo-render';

export interface SpinnerOptions {
  partitions: number[];
  cx?: number;
  cy?: number;
  r?: number;
  /** Optional pre-computed sector labels, one per partition. Defaults to '1'…'n'. */
  labels?: string[];
  /** Optional caption translated by the caller (e.g. "Sector"). */
  legendCaption?: string;
  onChange(partitions: number[]): void;
}

export interface SpinnerHandle {
  group: SVGGElement;
  setPartitions(p: number[]): void;
  destroy(): void;
}

const MIN_FRACTION = 0.02;

export function createSpinner(parent: SVGGElement, opts: SpinnerOptions): SpinnerHandle {
  const group = createSvgEl('g', { class: 'pb-spinner' });
  parent.appendChild(group);
  let current = [...opts.partitions];
  const cx = opts.cx ?? 0;
  const cy = opts.cy ?? 0;
  const r = opts.r ?? 2;
  const handleR = Math.max(6, r * 0.05);
  const labelR = r * 0.6;

  function arcPath(startAngle: number, endAngle: number): string {
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  }

  function boundaryAngles(): number[] {
    const total = current.reduce((a, b) => a + b, 0) || 1;
    const angles: number[] = [-Math.PI / 2];
    let a = -Math.PI / 2;
    for (const part of current) {
      a += (part / total) * Math.PI * 2;
      angles.push(a);
    }
    return angles;
  }

  function pointToAngle(px: number, py: number): number {
    return Math.atan2(py - cy, px - cx);
  }

  function normalizeOffset(a: number, anchor: number): number {
    let d = a - anchor;
    while (d <= 0) d += Math.PI * 2;
    while (d > Math.PI * 2) d -= Math.PI * 2;
    return d;
  }

  function svgRoot(): SVGSVGElement | null {
    let n: Node | null = parent;
    while (n && n.nodeName !== 'svg') n = n.parentNode;
    return n as SVGSVGElement | null;
  }

  function clientToSvgPoint(clientX: number, clientY: number): { x: number; y: number } {
    const svg = svgRoot();
    if (!svg) return { x: clientX, y: clientY };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: clientX, y: clientY };
    const local = pt.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  function attachHandle(handle: SVGCircleElement, boundaryIndex: number): void {
    let pointerId: number | null = null;
    const onPointerMove = (ev: PointerEvent): void => {
      if (pointerId === null || ev.pointerId !== pointerId) return;
      const { x, y } = clientToSvgPoint(ev.clientX, ev.clientY);
      const angle = pointToAngle(x, y);
      const angles = boundaryAngles();
      const prevAnchor = angles[boundaryIndex - 1]!;
      const nextAnchor = angles[boundaryIndex + 1]!;
      const totalSpan = normalizeOffset(nextAnchor, prevAnchor);
      const newOffset = normalizeOffset(angle, prevAnchor);
      const min = totalSpan * MIN_FRACTION;
      const clamped = Math.max(min, Math.min(totalSpan - min, newOffset));
      const total = current.reduce((a, b) => a + b, 0) || 1;
      const newLeft = (clamped / (Math.PI * 2)) * total;
      const newRight = ((totalSpan - clamped) / (Math.PI * 2)) * total;
      current[boundaryIndex - 1] = newLeft;
      current[boundaryIndex] = newRight;
      render();
      opts.onChange(current);
    };
    const onPointerUp = (ev: PointerEvent): void => {
      if (pointerId === null || ev.pointerId !== pointerId) return;
      handle.releasePointerCapture(pointerId);
      pointerId = null;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    handle.addEventListener('pointerdown', (ev: PointerEvent) => {
      pointerId = ev.pointerId;
      handle.setPointerCapture(pointerId);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
      ev.preventDefault();
    });
  }

  function render(): void {
    group.replaceChildren();
    const angles = boundaryAngles();
    const total = current.reduce((a, b) => a + b, 0) || 1;
    const labels = opts.labels;
    for (let i = 0; i < current.length; i++) {
      const a0 = angles[i]!;
      const a1 = angles[i + 1]!;
      const sector = createSvgEl('path', {
        class: `pb-spinner-sector pb-spinner-sector-${i % 4}`,
        d: arcPath(a0, a1),
      });
      group.appendChild(sector);
      const mid = (a0 + a1) / 2;
      const lblText = labels?.[i] ?? String(i + 1);
      const lbl = createSvgEl('text', {
        class: 'pb-spinner-label',
        x: cx + labelR * Math.cos(mid),
        y: cy + labelR * Math.sin(mid),
        'dominant-baseline': 'central',
      });
      lbl.textContent = `${lblText} (${((current[i]! / total) * 100).toFixed(0)}%)`;
      group.appendChild(lbl);
    }
    // draggable boundary handles (skip first/last shared boundary at top)
    for (let i = 1; i < current.length; i++) {
      const a = angles[i]!;
      const handle = createSvgEl('circle', {
        class: 'pb-spinner-handle',
        cx: cx + r * Math.cos(a),
        cy: cy + r * Math.sin(a),
        r: handleR,
      });
      group.appendChild(handle);
      attachHandle(handle, i);
    }
  }

  render();

  // legend lives outside of the disc area
  if (opts.legendCaption !== undefined) {
    const legend = createSvgEl('g', { class: 'pb-spinner-legend' });
    const labels = opts.labels;
    for (let i = 0; i < current.length; i++) {
      const y = cy + r + 30 + i * 18;
      const sw = createSvgEl('rect', {
        class: `pb-spinner-legend-swatch pb-spinner-sector-${i % 4}`,
        x: cx - r,
        y: y - 8,
        width: 14,
        height: 14,
        rx: 2,
      });
      const txt = createSvgEl('text', {
        class: 'pb-spinner-legend-text',
        x: cx - r + 20,
        y,
        'dominant-baseline': 'central',
      });
      txt.textContent = labels?.[i] ?? `${opts.legendCaption} ${i + 1}`;
      legend.appendChild(sw);
      legend.appendChild(txt);
    }
    group.appendChild(legend);
  }

  return {
    group,
    setPartitions(p) {
      current = [...p];
      render();
      opts.onChange(current);
    },
    destroy() {
      group.remove();
    },
  };
}
